import { FC, ReactNode } from 'react';
import MuiListItem, { ListItemProps as MuiListItemProps } from '@mui/material/ListItem';
import { styled } from '@mui/material/styles';
import { handleBreakpoints, ResponsiveStyleValue, SxProps } from '@mui/system';
import isNumber from 'lodash/isNumber';
import isNil from 'lodash/isNil';

export interface FieldRowProps extends Omit<MuiListItemProps, 'label' | 'value'> {
  label?: ReactNode;
  value?: ReactNode;
  /**
   * Label width. Number is considered as pixels, string can contain any valid CSS value (%, em, etc.).
   */
  labelWidth?: ResponsiveStyleValue<number | string>;
  color?: 'internal';
  divider?: boolean;
  labelAlign?: 'top' | 'center' | 'bottom';
  sx?: SxProps;
}

const convertVerticalAlign = (t: string) => {
  switch (t) {
    case 'top':
      return 'flex-start';
    case 'middle':
      return 'center';
    case 'bottom':
      return 'flex-end';
    default:
      return 'flex-start';
  }
};

const FieldRowRoot = styled(MuiListItem, {
  name: 'O2FieldRow',
  slot: 'Root',
  shouldForwardProp: (prop) =>
    prop !== 'disablePadding' && prop !== 'disableGutters' && prop !== 'verticalAlign' && prop !== 'labelAlign',
})<any>(({ theme, color, dense, disablePadding, disableGutters, verticalAlign }) => ({
  display: 'flex',
  alignItems: convertVerticalAlign(verticalAlign),
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  ...(color === 'internal' && {
    backgroundColor: theme.palette.background.internal,
  }),
  ...(!disablePadding && {
    paddingTop: 10,
    paddingBottom: 10,
    ...(dense && {
      paddingTop: 4,
      paddingBottom: 4,
    }),
    ...(!disableGutters && {
      paddingLeft: 8,
      paddingRight: 8,
    }),
  }),
}));

const FieldRowLabel = styled('div', {
  name: 'O2FieldRow',
  slot: 'Label',
  shouldForwardProp: (prop) => prop !== 'labelWidth',
})<any>(({ labelWidth, theme }) => ({
  flexBasis: '30%',
  flexShrink: 0,
  flexGrow: 0,
  fontWeight: theme.typography.fontWeightMedium,
  paddingRight: '8px',
  ...handleBreakpoints({ theme }, labelWidth, (propValue) => ({
    flexBasis: propValue,
  })),
}));

const FieldRowValue = styled('div', {
  name: 'O2FieldRow',
  slot: 'Value',
})(({ theme }) => ({
  flexBasis: '68%',
  flexShrink: 1,
  flexGrow: 1,
  color: theme.palette.text.secondary,
  [theme.breakpoints.down('sm')]: {
    flexBasis: '100%',
  },
}));

/**
 * Structured view of `label - value` pairs. Use this if you want to display structured data, but full size table is considered "too much".
 * @deprecated
 */
const FieldRow: FC<FieldRowProps> = ({ label, value, labelWidth, labelAlign = 'top', ...props }) => {
  let computedLabelWidth;

  if (isNumber(labelWidth) || !isNaN(Number(labelWidth))) {
    // Number is like pixels
    computedLabelWidth = `${labelWidth}px`;
  } else {
    // Other value as is
    computedLabelWidth = labelWidth;
  }

  if (isNil(label) && isNil(value))
    // Don't show anything, if no content is set.
    return null;
  else
    return (
      <FieldRowRoot labelAlign={labelAlign} {...props}>
        {label && <FieldRowLabel labelWidth={computedLabelWidth}>{label}</FieldRowLabel>}
        {value && <FieldRowValue>{value}</FieldRowValue>}
      </FieldRowRoot>
    );
};

export default FieldRow;
